import React, { useMemo, useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
// import './individualNews.scss'
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from "@mui/icons-material/Download";
import ThreatWordCloud from "../components/ThreatWordCloud.jsx";
import './modalMain.scss'
import { Bar, Line } from 'react-chartjs-2';
import { Colors } from 'chart.js';
// import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js/auto';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    Colors
);
ChartJS.register(Colors)

export const PopOutChartModal = ({
    isOpen,
    onClose,
    chartData,
    type,
    title = "Expanded analytical view"
}) => {
    const chartDivRef = useRef(null);
    const chartRef = useRef(null);
    const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

    const handleDownload = () => {
        const fileName =
            title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "") || "chart";

        if (type === "WORDCLOUD") {
            const sourceCanvas = chartDivRef.current?.querySelector("canvas");

            if (!sourceCanvas) {
                console.warn("Unable to export word cloud: canvas not found.");
                return;
            }

            const exportCanvas = document.createElement("canvas");
            const context = exportCanvas.getContext("2d");

            if (!context) {
                console.warn("Unable to create word-cloud export canvas.");
                return;
            }

            const scale = 2;

            exportCanvas.width = sourceCanvas.width * scale;
            exportCanvas.height = sourceCanvas.height * scale;

            context.scale(scale, scale);

            // Add a solid background so the exported PNG matches the modal
            // instead of saving with a transparent or black background.
            context.fillStyle = "#0b1624";
            context.fillRect(
                0,
                0,
                sourceCanvas.width,
                sourceCanvas.height
            );

            context.drawImage(
                sourceCanvas,
                0,
                0,
                sourceCanvas.width,
                sourceCanvas.height
            );

            const link = document.createElement("a");
            link.download = `${fileName}.png`;
            link.href = exportCanvas.toDataURL("image/png", 1);
            link.click();

            return;
        }

        const chartInstance = chartRef.current;

        if (!chartInstance) {
            console.warn("Unable to export chart: chart instance not found.");
            return;
        }

        const link = document.createElement("a");
        link.download = `${fileName}.png`;
        link.href = chartInstance.toBase64Image("image/png", 1);
        link.click();
    };

    useEffect(() => {
        if (!chartDivRef.current || !isOpen) return;

        const resizeObserver = new ResizeObserver(entries => {
            const rect = entries[0].contentRect;

            setChartSize({
                width: rect.width,
                height: rect.height
            });
        });

        resizeObserver.observe(chartDivRef.current);

        return () => resizeObserver.disconnect();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const previousOverflow = document.body.style.overflow;

        document.body.style.overflow = "hidden";

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose]);

    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 100,
        animation: {
            duration: 350,
            easing: "easeOutQuart"
        },
        interaction: {
            mode: "index",
            intersect: false
        },
        elements: {
            line: {
                tension: 0.34,
                borderWidth: 2
            },
            point: {
                radius: 2,
                hoverRadius: 5
            }
        },
        plugins: {
            legend: {
                display: true,
                position: "top",
                align: "start",
                labels: {
                    color: "rgba(244, 247, 251, 0.72)",
                    boxWidth: 12,
                    boxHeight: 3,
                    padding: 18,
                    usePointStyle: true,
                    pointStyle: "line",
                    font: {
                        size: 12
                    }
                }
            },
            tooltip: {
                backgroundColor: "rgba(7, 14, 24, 0.97)",
                borderColor: "rgba(255, 255, 255, 0.14)",
                borderWidth: 1,
                titleColor: "#f4f7fb",
                bodyColor: "rgba(244, 247, 251, 0.78)",
                padding: 12,
                displayColors: true
            }
        },
        layout: {
            padding: {
                top: 8,
                right: 12,
                bottom: 8,
                left: 4
            }
        },
        scales: {
            x: {
                grid: {
                    color: "rgba(255, 255, 255, 0.035)"
                },
                border: {
                    color: "rgba(255, 255, 255, 0.08)"
                },
                ticks: {
                    color: "rgba(244, 247, 251, 0.56)",
                    maxRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 12,
                    padding: 8
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: "rgba(255, 255, 255, 0.045)"
                },
                border: {
                    display: false
                },
                ticks: {
                    color: "rgba(244, 247, 251, 0.56)",
                    padding: 10
                }
            }
        }
    };

    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 100,
        animation: {
            duration: 350,
            easing: "easeOutQuart"
        },
        interaction: {
            mode: "index",
            intersect: false
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: "rgba(7, 14, 24, 0.97)",
                borderColor: "rgba(255, 255, 255, 0.14)",
                borderWidth: 1,
                titleColor: "#f4f7fb",
                bodyColor: "rgba(244, 247, 251, 0.78)",
                padding: 12,
                displayColors: false
            }
        },
        layout: {
            padding: {
                top: 8,
                right: 12,
                bottom: 12,
                left: 4
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                border: {
                    color: "rgba(255, 255, 255, 0.08)"
                },
                ticks: {
                    color: "rgba(244, 247, 251, 0.58)",
                    autoSkip: false,
                    maxRotation: 35,
                    minRotation: 0,
                    padding: 10,
                    font: {
                        size: 11
                    }
                }
            },
            y: {
                beginAtZero: true,
                grace: "10%",
                grid: {
                    color: "rgba(255, 255, 255, 0.045)"
                },
                border: {
                    display: false
                },
                ticks: {
                    color: "rgba(244, 247, 251, 0.56)",
                    padding: 10,
                    precision: 0
                }
            }
        }
    };


    const chart = useMemo(() => {
        // For word cloud, consider data empty if null or empty object
        if (type === "WORDCLOUD" && (!chartData || Object.keys(chartData).length === 0)) {
            return (
                <div className="chartdiv modalChartDiv">
                    <div className="modalChartEmpty">
                        <span>Preparing visualization</span>
                        <p>The expanded language view is being sized for display.</p>
                    </div>
                </div>
            );
        }

        // For BAR/LINE, ensure datasets exist
        if ((type === "BAR" || type === "LINE") && (!chartData || !chartData.datasets)) {
            return (
                <div className="chartdiv modalChartDiv">
                    <p style={{ color: "#fff", textAlign: "center" }}>
                        No data available to display.
                    </p>
                </div>
            );
        }

        if (type === "BAR") {
            return (
                <div className="chartdiv modalChartDiv" ref={chartDivRef}>
                    <Bar ref={chartRef} data={chartData} options={barOptions} />
                </div>
            );
        }
        if (type === "LINE") {
            return (
                <div className="chartdiv modalChartDiv" ref={chartDivRef}>
                    <Line ref={chartRef} data={chartData} options={lineOptions} />
                </div>
            );
        }
        if (type === "WORDCLOUD") {
            return (
                <div className="chartdiv modalChartDiv" ref={chartDivRef}>
                    {chartSize.width > 0 && chartSize.height > 0 ? (
                        <ThreatWordCloud
                            data={chartData}
                            width={chartSize.width}
                            height={chartSize.height}
                            options={{
                                events: [],
                                animation: false,
                                plugins: { tooltip: { enabled: false } }
                            }}
                        />
                    ) : (
                        <div className="modalChartEmpty">
                            <span>Visualization unavailable</span>
                            <p>No data is available for the current analytical view.</p>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    }, [type, chartData, chartSize]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div
            className="modalMainContainer"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <section
                className="chartContainer"
                role="dialog"
                aria-modal="true"
                aria-labelledby="expanded-chart-title"
            >
                <header className="modalheader">
                    <div className="modalTitleBlock">
                        <span className="modalEyebrow">Expanded analytical view</span>
                        <h1 id="expanded-chart-title">{title}</h1>
                        <p>
                            Review the visualization at full scale or export it for
                            reporting.
                        </p>
                    </div>

                    <div className="modalActions">
                        <button
                            type="button"
                            className="modalActionButton"
                            onClick={handleDownload}
                            aria-label={`Download ${title} as PNG`}
                            title="Download PNG"
                        >
                            <DownloadIcon aria-hidden="true" />
                            <span>Export PNG</span>
                        </button>

                        <button
                            type="button"
                            className="modalCloseButton"
                            onClick={onClose}
                            aria-label="Close expanded chart"
                            title="Close"
                        >
                            <CloseIcon aria-hidden="true" />
                        </button>
                    </div>
                </header>

                <div className="modalChartWorkspace">
                    <div className={`modalChartFrame modalChartFrame--${type.toLowerCase()}`}>
                        {chart}
                    </div>

                    <footer className="modalChartFooter">
                        <span>Weathervane Intel</span>
                        <span>Operational Analytics</span>
                    </footer>
                </div>
            </section>
        </div>,
        document.getElementById("portal")
    );
}

